/**
 * Formatting helpers. `es-MX` throughout — `00` §2 flags `en-us` as a bug, and
 * a date rendered `08/21/2026` to a Mexican seller is not a cosmetic problem:
 * it is read as 8 de… something, and acted on.
 */

const LOCALE = "es-MX";

const dateOnly = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateAndTime = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const timeOnly = new Intl.DateTimeFormat(LOCALE, {
  hour: "2-digit",
  minute: "2-digit",
});

/** `14:32` — for something that happened minutes ago, like a draft being saved. */
export function formatTime(when: Date | null): string {
  return when === null ? "" : timeOnly.format(when);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : dateOnly.format(date);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : dateAndTime.format(date);
}

/**
 * "hace 3 días" / "en 2 horas".
 *
 * The timeline is read by date, but the *interesting* question a seller asks of
 * it is "how long ago", and computing that from a formatted date is work they
 * should not have to do.
 */
const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 1000 * 60 * 60 * 24 * 365],
  ["month", 1000 * 60 * 60 * 24 * 30],
  ["day", 1000 * 60 * 60 * 24],
  ["hour", 1000 * 60 * 60],
  ["minute", 1000 * 60],
];

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const delta = then - Date.now();
  for (const [unit, ms] of UNITS) {
    if (Math.abs(delta) >= ms) {
      return relative.format(Math.round(delta / ms), unit);
    }
  }
  return relative.format(0, "minute");
}

const DAY_MS = 1000 * 60 * 60 * 24;

const weekday = new Intl.DateTimeFormat(LOCALE, { weekday: "long" });

const numericDate = new Intl.DateTimeFormat(LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase(LOCALE) + text.slice(1);
}

/**
 * "Hoy", "Ayer", "Lunes" or "18/09/2026" — the way WhatsApp names a day, on the
 * chip above the first message of it and beside a chat in the inbox list.
 *
 * Counted in calendar days, not in 24-hour spans: a message from 11 p.m. is
 * "Ayer" at 1 a.m., the way the seller's own phone says it.
 */
export function formatChatDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  // Rounded, because a day that crosses a DST change is 23 or 25 hours long.
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / DAY_MS);
  if (days === 0 || days === 1) return capitalize(relative.format(-days, "day"));
  if (days > 1 && days < 7) return capitalize(weekday.format(date));
  return numericDate.format(date);
}

/** "12:31 p.m." — the time alone, as a WhatsApp bubble prints it. */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : timeOnly.format(date);
}

/** Beside a chat in the list: the time if it was today, the day otherwise. */
export function formatChatStamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return startOfDay(date) === startOfDay(new Date())
    ? timeOnly.format(date)
    : formatChatDay(iso);
}

const kilobytes = new Intl.NumberFormat(LOCALE, {
  style: "unit",
  unit: "kilobyte",
  maximumFractionDigits: 0,
});

const megabytes = new Intl.NumberFormat(LOCALE, {
  style: "unit",
  unit: "megabyte",
  maximumFractionDigits: 1,
});

/** "340 kB", "2.4 MB" — a file's weight, the way WhatsApp prints it. */
export function formatFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? kilobytes.format(Math.max(1, Math.round(bytes / 1024)))
    : megabytes.format(bytes / (1024 * 1024));
}

const ago = new Intl.RelativeTimeFormat(LOCALE, { numeric: "always" });

/**
 * "hace 3 días", always in that form — never "ayer" or "el mes pasado".
 *
 * For the timeline's "creado el … · hace …", where the idiomatic words would
 * sit after a date and stop reading as a duration. Past only: a browser clock
 * a little behind the server's does not make a row come from the future, and
 * under a minute is still "hace 1 minuto" rather than "hace 0 minutos".
 */
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const elapsed = Math.max(0, Date.now() - then);
  for (const [unit, ms] of UNITS) {
    if (elapsed >= ms) return ago.format(-Math.round(elapsed / ms), unit);
  }
  return ago.format(-1, "minute");
}

/**
 * A Mexican number as it is said and dialled here: ten digits, no country code.
 *
 * Numbers are stored in E.164 — `+528181504218` — because that is what Twilio
 * and WhatsApp take. On a contact card the `+52` is noise in front of every
 * number in the book. The old mobile marker goes with it: `+521…` and `+52…`
 * are the same handset.
 *
 * Display only. Anything that dials, links or sends keeps the stored value —
 * and a number from another country is shown whole, since without its code it
 * would read as a local one.
 */
export function formatPhone(e164: string): string {
  return /^\+521?(\d{10})$/.exec(e164)?.[1] ?? e164;
}
